/**
 * The formatter registry.
 *
 * Every number, date and identifier a person reads passes through exactly one
 * of these functions. Currency, dates and decimals have each been formatted in
 * three places at once in this codebase and the copies disagreed, so a lint
 * rule forbids Intl and toLocaleString anywhere else, and docs/NUMBERS.md
 * explains the doctrine.
 *
 * Two rules shape the signatures.
 *
 * 1. Decimals arrive as strings. They cross the driver as ::text precisely so
 *    they never become a float, and turning one into a Number here to hand to
 *    Intl would undo that at the last step. Intl formats a decimal string
 *    exactly, so it never has to.
 *
 * 2. "Not set" and "not permitted to see" are different answers, and a
 *    formatter that returned an em dash for both would flatten them. Absence
 *    is described by `absent()`, which names the reason, and the caller
 *    decides how to show it.
 */

/**
 * ES2023 lets Intl.NumberFormat format a decimal string exactly, without a
 * float in the middle. TypeScript's lib types still say number | bigint, so the
 * capability is declared once here rather than cast at every call site - a cast
 * is a promise the compiler cannot check, and this one is checkable.
 */
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Intl {
    interface NumberFormat {
      format(value: number | bigint | string): string;
    }
  }
}

export interface FormatContext {
  /** BCP-47, e.g. 'en-GB' or 'ar-AE'. */
  locale: string;
  /** IANA zone the workspace works in, e.g. 'Asia/Dubai'. */
  timeZone: string;
  /** ISO-4217, e.g. 'AED'. */
  currency: string;
  /**
   * Western Arabic (0-9) unless the workspace opts into Eastern Arabic.
   * Language and numeral system are separate choices: an Arabic interface with
   * Latin digits is the common preference in the Gulf.
   */
  numerals: 'latn' | 'arab';
  /** A secondary Hijri date is shown beside Gregorian dates by default here. */
  secondaryCalendar: 'islamic-umalqura' | 'none';
  /** Injected so tests do not depend on the wall clock. */
  now?: Date;
}

export const DEFAULT_FORMAT_CONTEXT: FormatContext = {
  locale: 'en-GB',
  timeZone: 'Asia/Dubai',
  currency: 'AED',
  numerals: 'latn',
  secondaryCalendar: 'islamic-umalqura',
};

/** Regional defaults that hold whatever language the interface is in. */
export const REGIONAL_DEFAULTS = {
  /** Sunday to Thursday. 0 is Sunday. */
  workingDays: [0, 1, 2, 3, 4] as const,
  weekStartsOn: 0,
  currency: 'AED',
  timeZone: 'Asia/Dubai',
  phoneCountry: 'AE',
  phoneDialCode: '+971',
} as const;

/**
 * The regions offered in the profile.
 *
 * A list rather than a free-text box. A locale typed by hand is a locale that
 * is wrong in a way nobody sees until a date reads 03/04 and two people
 * disagree about which month it is - and `Intl` accepts almost anything
 * without complaining, falling back silently to something else.
 *
 * Deliberately short. These are the places this product is sold, and a list of
 * six hundred locales is a list nobody reads.
 */
export const OFFERED_LOCALES = [
  { locale: 'en-GB', numerals: 'latn' as const },
  { locale: 'en-US', numerals: 'latn' as const },
  { locale: 'en-IN', numerals: 'latn' as const },
  { locale: 'ar-AE', numerals: 'arab' as const },
  { locale: 'ar-EG', numerals: 'arab' as const },
  { locale: 'fr-FR', numerals: 'latn' as const },
] as const;

/**
 * The time zones offered in the profile.
 *
 * Same reasoning, and one more: a zone decides which calendar day a timestamp
 * falls on, so getting it wrong moves an activity to yesterday for one person
 * and not for another.
 */
export const OFFERED_TIME_ZONES = [
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
] as const;

function localeWithNumerals(ctx: FormatContext): string {
  return `${ctx.locale}-u-nu-${ctx.numerals}`;
}

const numberFormats = new Map<string, Intl.NumberFormat>();
function numberFormat(ctx: FormatContext, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${localeWithNumerals(ctx)}|${ctx.currency}|${JSON.stringify(options)}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(localeWithNumerals(ctx), options);
    numberFormats.set(key, format);
  }
  return format;
}

const dateFormats = new Map<string, Intl.DateTimeFormat>();
function dateFormat(ctx: FormatContext, options: Intl.DateTimeFormatOptions, calendar?: string): Intl.DateTimeFormat {
  const locale = calendar ? `${localeWithNumerals(ctx)}-ca-${calendar}` : localeWithNumerals(ctx);
  const key = `${locale}|${ctx.timeZone}|${JSON.stringify(options)}`;
  let format = dateFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { timeZone: ctx.timeZone, ...options });
    dateFormats.set(key, format);
  }
  return format;
}

// ---------------------------------------------------------------------------
// Absence
// ---------------------------------------------------------------------------

export type AbsenceReason =
  /** The field exists and holds no value. */
  | 'unset'
  /** Field-level security hides it from this person. */
  | 'hidden'
  /** The field does not apply to this record at all. */
  | 'not-applicable'
  /** A scheduled next step that has not been scheduled. */
  | 'nothing-scheduled'
  /**
   * A reference that points at something that could not be read.
   *
   * A deleted record, or a value the server has not resolved into a name. Kept
   * separate from 'unset' because a field holding a broken reference and a
   * field holding nothing are different problems with different fixes - and
   * showing the raw identifier instead is not an option, since a UUID must
   * never reach a person's eyes.
   */
  | 'unresolved'
  /**
   * There were no rows to total.
   *
   * `sum` of nothing is null, and null is not zero. Kept apart from 'unset'
   * because "Not set" describes a field somebody left blank, and this is a
   * question that had nothing to answer it - different words for different
   * situations (P-5).
   */
  | 'no-rows';

export interface Absent {
  kind: 'absent';
  reason: AbsenceReason;
  /** What to render. Never an em dash for everything. */
  text: string;
}

const ABSENCE_TEXT: Record<AbsenceReason, { en: string; ar: string }> = {
  unset: { en: 'Not set', ar: 'غير محدد' },
  hidden: { en: 'Hidden', ar: 'مخفي' },
  'not-applicable': { en: 'Not applicable', ar: 'لا ينطبق' },
  'nothing-scheduled': { en: 'Nothing scheduled', ar: 'لا يوجد إجراء مجدول' },
  unresolved: { en: 'Could not be shown', ar: 'تعذر عرضه' },
  'no-rows': { en: 'Nothing to total', ar: 'لا يوجد ما يُجمع' },
};

export function absent(reason: AbsenceReason, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): Absent {
  const entry = ABSENCE_TEXT[reason];
  return { kind: 'absent', reason, text: ctx.locale.startsWith('ar') ? entry.ar : entry.en };
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * Money.
 *
 * `amount` is the decimal exactly as the database produced it. Intl formats a
 * decimal string without going through a float, so a fourteen-digit total is
 * still exact when it reaches the screen.
 */
export function formatMoney(
  amount: string,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
  options: {
    currency?: string;
    showDecimals?: boolean;
    sign?: boolean;
    /**
     * Draw the digits without the currency, for a caller that sets the code
     * itself - a hero figure puts the currency small and before the number so
     * a column of figures aligns on the digits.
     *
     * The fraction digits still come from the currency rather than from a
     * constant 2: a caller stripping the symbol by hand would silently give
     * JPY two decimal places it does not have.
     */
    symbol?: boolean;
  } = {},
): string {
  assertDecimalString(amount, 'formatMoney');
  const currency = options.currency ?? ctx.currency;
  const currencyDigits = numberFormat(ctx, { style: 'currency', currency })
    .resolvedOptions()
    .minimumFractionDigits ?? 2;
  const digits = options.showDecimals === false ? 0 : currencyDigits;
  if (options.symbol === false) {
    return numberFormat(ctx, {
      style: 'decimal',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: options.sign ? 'exceptZero' : 'auto',
    }).format(amount);
  }
  return numberFormat(ctx, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: options.sign ? 'exceptZero' : 'auto',
  }).format(amount);
}

/** A plain count: rows, records, members. Always grouped, never abbreviated. */
export function formatCount(value: number, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  return numberFormat(ctx, { maximumFractionDigits: 0 }).format(value);
}

/** A decimal that is not money: a score, a weight, a conversion factor. */
export function formatDecimal(
  value: string,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  assertDecimalString(value, 'formatDecimal');
  return numberFormat(ctx, {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(value);
}

/** `ratio` is a fraction: 0.42 renders as 42%. */
export function formatPercent(
  ratio: number,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
  options: { decimals?: number; sign?: boolean } = {},
): string {
  return numberFormat(ctx, {
    style: 'percent',
    minimumFractionDigits: options.decimals ?? 0,
    maximumFractionDigits: options.decimals ?? 0,
    signDisplay: options.sign ? 'exceptZero' : 'auto',
  }).format(ratio);
}

/** Bytes, for file attachments and export downloads. */
export function formatFileSize(bytes: number, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return numberFormat(ctx, {
    style: 'unit',
    unit: units[unit],
    unitDisplay: 'short',
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value);
}

// ---------------------------------------------------------------------------
// Dates and times
// ---------------------------------------------------------------------------

export const DAY_MS = 86_400_000;

function toDate(value: string | Date): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Not a date: ${String(value)}. Timestamps cross the API as ISO-8601 strings.`);
  }
  return date;
}

/** Absolute date in the workspace's zone: "11 Sept 2026". */
export function formatDate(value: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  return dateFormat(ctx, { dateStyle: 'medium' }).format(toDate(value));
}

/** Absolute date and time: "11 Sept 2026, 14:30". */
export function formatDateTime(value: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  return dateFormat(ctx, { dateStyle: 'medium', timeStyle: 'short' }).format(toDate(value));
}

/** Time alone, for a row that already states its date. */
export function formatTime(value: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  return dateFormat(ctx, { timeStyle: 'short' }).format(toDate(value));
}

/**
 * The secondary Hijri date shown beside a Gregorian one.
 *
 * Regional default here regardless of interface language. Returns null when
 * the workspace has turned it off, so a caller cannot accidentally render an
 * empty parenthesis.
 */
export function formatSecondaryDate(
  value: string | Date,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
): string | null {
  if (ctx.secondaryCalendar === 'none') return null;
  return dateFormat(ctx, { dateStyle: 'medium' }, ctx.secondaryCalendar).format(toDate(value));
}

/**
 * Relative under seven days, absolute beyond.
 *
 * The comparison is made in the workspace's zone, not the viewer's: a deal
 * closing "today" means today where the business is. Doing this in UTC is the
 * defect the east/west test matrix exists to catch.
 */
export function formatRelativeDate(
  value: string | Date,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
): string {
  const date = toDate(value);
  const now = ctx.now ?? new Date();
  const days = calendarDaysBetween(now, date, ctx.timeZone);
  if (Math.abs(days) >= 7) return formatDate(date, ctx);
  const rtf = new Intl.RelativeTimeFormat(localeWithNumerals(ctx), { numeric: 'auto' });
  return rtf.format(days, 'day');
}

/** Whole calendar days from `from` to `to`, counted in `timeZone`. */
export function calendarDaysBetween(from: string | Date, to: string | Date, timeZone: string): number {
  const a = startOfDayUtcFor(toDate(from), timeZone);
  const b = startOfDayUtcFor(toDate(to), timeZone);
  return Math.round((b - a) / DAY_MS);
}

/** The instant a calendar day starts, expressed as milliseconds since epoch. */
export function startOfDayUtcFor(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00';
  return Date.parse(`${get('year')}-${get('month')}-${get('day')}T00:00:00Z`);
}

/** The calendar date in the workspace zone, as YYYY-MM-DD. */
export function workspaceDate(value: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ctx.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(toDate(value));
}

/** An instant as a datetime-local input value (YYYY-MM-DDTHH:mm), on the workspace's clock. */
export function workspaceDateTimeInput(value: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ctx.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(toDate(value));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * A datetime-local value read on the workspace's clock, as an ISO instant.
 * The inverse of `workspaceDateTimeInput`: the zone's offset at that moment is
 * found by formatting a guess and correcting by the difference, twice so a
 * time beside a daylight-saving change settles on the right side of it.
 */
export function instantFromWorkspaceInput(local: string, timeZone: string): string | null {
  const wall = Date.parse(`${local}:00Z`);
  if (Number.isNaN(wall)) return null;
  let guess = wall;
  for (let pass = 0; pass < 2; pass += 1) {
    const shown = Date.parse(`${workspaceDateTimeInput(new Date(guess), { ...DEFAULT_FORMAT_CONTEXT, timeZone })}:00Z`);
    guess += wall - shown;
  }
  return new Date(guess).toISOString();
}

export type DueState = 'overdue' | 'today' | 'this-week' | 'later';

/** Which section of My Work an instant belongs in. */
export function dueState(due: string | Date, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): DueState {
  const days = calendarDaysBetween(ctx.now ?? new Date(), due, ctx.timeZone);
  return dueStateForDays(days);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Which section a DUE DATE belongs in.
 *
 * A due date has no time and no zone: "the fourteenth" is the fourteenth
 * wherever you are. Passing one through an instant-based comparison converts
 * it as though it were midnight UTC, and west of UTC that lands on the
 * thirteenth - so a step due today shows as overdue for everybody in the
 * Americas. This compares calendar days as calendar days and never builds an
 * instant from either side.
 */
export function dueStateForDate(dueDate: string, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): DueState {
  if (!DATE_ONLY.test(dueDate)) {
    throw new Error(
      `dueStateForDate expects a calendar date like 2026-09-14, not ${JSON.stringify(dueDate)}. ` +
        `Use dueState for a timestamp.`,
    );
  }
  const today = workspaceDate(ctx.now ?? new Date(), ctx);
  return dueStateForDays(daysBetweenCalendarDates(today, dueDate));
}

function dueStateForDays(days: number): DueState {
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'this-week';
  return 'later';
}

/** Whole days between two YYYY-MM-DD dates, with no zone involved at all. */
export function daysBetweenCalendarDates(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / DAY_MS);
}

export type PartOfDay = 'morning' | 'afternoon' | 'evening';

/**
 * Morning, afternoon or evening in the workspace's zone.
 *
 * Here rather than on the screen that greets somebody, for the same reason
 * every other displayed value is: the screen ran `toLocaleString` on the
 * browser's own clock, so a rep in Dubai looking at a workspace kept in London
 * was wished good evening over lunch. There is one clock in this system and it
 * is the workspace's.
 */
export function partOfDay(
  value: string | Date = new Date(),
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
): PartOfDay {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: ctx.timeZone,
      hour: '2-digit',
      // h23 rather than hour12: false - some locales render midnight as 24
      // under the latter, and 24 is not less than 12.
      hourCycle: 'h23',
    }).format(toDate(value)),
  );
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * The weekday and the minute-of-day an instant falls on, where the office is.
 *
 * Here rather than beside the code that needs it, because this is zone
 * arithmetic and zone arithmetic lives in one place (NUMBERS.md). The SLA
 * clock walks a working week and has to ask this question on every step; a
 * second `Intl.DateTimeFormat` next to that walk would be a second answer to
 * "what day is it there", and the two would disagree across a DST boundary.
 *
 * 0 is Sunday, matching `REGIONAL_DEFAULTS.workingDays` and `tenant.working_days`.
 */
export function localWeekdayAndMinute(
  value: string | Date,
  timeZone: string,
): { weekday: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(toDate(value));
  const read = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return {
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(read('weekday')),
    minute: Number(read('hour')) * 60 + Number(read('minute')),
  };
}

/**
 * Is this a working day?
 *
 * The days are passed in, defaulting to the regional constant. They used to be
 * the constant full stop - Sunday to Thursday, which is the Gulf - so a
 * European workspace's due dates and SLA clocks skipped the wrong two days.
 * The workspace's own week now lives on `tenant.working_days`, and this reads
 * whatever it is handed.
 */
export function isWorkingDay(
  value: string | Date,
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
  days: readonly number[] = REGIONAL_DEFAULTS.workingDays,
): boolean {
  const { weekday } = localWeekdayAndMinute(value, ctx.timeZone);
  return days.includes(weekday);
}

// ---------------------------------------------------------------------------
// Identity and contact
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * A reference to a record.
 *
 * A raw identifier is never shown to a person: it is unreadable, unsearchable
 * and unquotable down a phone line. When the name is missing the line is
 * omitted rather than filled with a UUID, which is why this returns null.
 */
export function formatRecordRef(name: string | null | undefined): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  if (isUuid(trimmed)) return null;
  return trimmed;
}

/**
 * A phone number in the region's convention.
 *
 * E.164 in, grouped out: +971501234567 becomes +971 50 123 4567. Anything not
 * recognised is returned unchanged rather than mangled - a number the product
 * cannot parse is still a number somebody has to dial.
 */
export function formatPhone(e164: string, ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): string {
  const digits = e164.replace(/[^\d+]/g, '');
  if (digits.startsWith(REGIONAL_DEFAULTS.phoneDialCode)) {
    const rest = digits.slice(REGIONAL_DEFAULTS.phoneDialCode.length);
    if (rest.length === 9) {
      return `${REGIONAL_DEFAULTS.phoneDialCode} ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
    }
  }
  if (digits.startsWith('+91') && digits.length === 13) {
    return `+91 ${digits.slice(3, 8)} ${digits.slice(8)}`;
  }
  void ctx;
  return e164;
}

/** Initials for an avatar, from a display name. Never more than two letters. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/**
 * Refuses anything that is not a decimal string.
 *
 * The failure this prevents is silent: a Number reaching a money formatter
 * looks right for small values and rounds wrongly for large ones, and nothing
 * about the output says which happened.
 */
export function assertDecimalString(value: string, where: string): void {
  if (typeof value !== 'string') {
    throw new Error(
      `${where} was given a ${typeof value}. Decimals cross the API as strings so they never become floats; see docs/NUMBERS.md.`,
    );
  }
  if (!DECIMAL_RE.test(value)) {
    throw new Error(`${where} was given ${JSON.stringify(value)}, which is not a decimal string.`);
  }
}

/**
 * Today, as the reader's workspace reckons it (D-354).
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC day, and a screen that
 * asks the server for "everything up to today" using it asks for yesterday in
 * Dubai for four hours every evening, and for tomorrow in Los Angeles for seven
 * hours every afternoon.
 *
 * Built from the formatter's own parts rather than by arithmetic, so there is
 * one place that knows which zone the workspace keeps.
 */
export function todayIn(ctx: FormatContext, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ctx.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const of = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${of('year')}-${of('month')}-${of('day')}`;
}

/** The same day, moved by whole days. Calendar arithmetic, never a clock. */
export function addDaysTo(day: string, days: number): string {
  const moved = new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000);
  return moved.toISOString().slice(0, 10);
}
